import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  Grid,
  IconButton,
  useTheme
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import L from 'leaflet';
import { LatLngExpression } from 'leaflet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../axiosInstance';

interface GPSInformationProps {
  gpsData?: any;
  latitude?: number;
  longitude?: number;
}

const GPSInformation: React.FC<GPSInformationProps> = ({ gpsData, latitude, longitude }) => {
  const theme = useTheme();
  const defaultPosition: [number, number] = [52.237049, 21.017532]; // Center of Poland
  const defaultZoomLevel = 5;
  const markerZoomLevel = 10;

  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultPosition);
  const [zoomLevel, setZoomLevel] = useState(defaultZoomLevel);
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1); // -1 means showing current location

  const modem = gpsData?.modem_gps || "Unknown";
  const currentGps = gpsData?.gps || "Unknown";
  const timeStamp = gpsData?.time_stamp_gps ? new Date(gpsData.time_stamp_gps).toLocaleString() : "Unknown";

  useEffect(() => {
    const fetchHistoricalData = async () => {
      if (gpsData?.box) {
        try {
          const response = await axiosInstance.get(`/boxpanel/historical-locations/${gpsData.box}`);
          setHistoricalData(response.data);
        } catch (error) {
          console.error('Error fetching historical data:', error);
        }
      }
    };
    fetchHistoricalData();
  }, [gpsData?.box]);

  const getCurrentCoordinates = () => {
    if (currentIndex === -1) {
      return { 
        lat: latitude, 
        lng: longitude, 
        time: timeStamp,
        gps: currentGps 
      };
    }
    const historical = historicalData[currentIndex];
    return {
      lat: parseFloat(historical.historical_gps.split('N')[0]),
      lng: parseFloat(historical.historical_gps.split('N')[1].replace('E', '')),
      time: new Date(historical.historical_time).toLocaleString(),
      gps: historical.historical_gps
    };
  };

  const navigateLocation = (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentIndex < historicalData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (direction === 'prev' && currentIndex >= 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentCoords = getCurrentCoordinates();

  const resetMap = () => {
    setMapCenter(defaultPosition);
    setZoomLevel(defaultZoomLevel);
  };

  const zoomToMarker = () => {
    const coords = getCurrentCoordinates();
    if (coords.lat !== undefined && coords.lng !== undefined) {
      setMapCenter([coords.lat, coords.lng]);
      setZoomLevel(markerZoomLevel);
    }
  };

  function MapController() {
    const map = useMap();
    map.setView(mapCenter, zoomLevel);
    return null;
  }

  return (
    <Card sx={{ border: `1px solid ${theme.palette.borders.primary}` }}>
      <CardHeader title="GPS Information" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Modem</Typography>
            <Typography variant="body2">{modem}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>GPS</Typography>
            <Typography variant="body2">{currentCoords.gps}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>Time</Typography>
            <Typography variant="body2">{currentCoords.time}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 40 }}>
              {currentIndex > -1 && (
                <IconButton 
                  color="primary" 
                  onClick={() => navigateLocation('prev')}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
            </Box>
            <Typography variant="body2" sx={{ minWidth: '120px', textAlign: 'center' }}>
              {currentIndex === -1 
                ? "Current Location" 
                : `Historical ${currentIndex + 1}/${historicalData.length}`
              }
            </Typography>
            {currentIndex < historicalData.length - 1 && (
              <IconButton 
                color="primary" 
                onClick={() => navigateLocation('next')}
              >
                <ArrowForwardIcon />
              </IconButton>
            )}
          </Box>
          <Box>
            <IconButton color="primary" onClick={resetMap}>
              <ZoomOut />
            </IconButton>
            <IconButton color="primary" onClick={zoomToMarker}>
              <ZoomInIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ height: '300px', width: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <MapContainer center={mapCenter as LatLngExpression} zoom={zoomLevel} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController />
            {currentCoords.lat !== undefined && currentCoords.lng !== undefined && (
              <Marker 
                position={[currentCoords.lat, currentCoords.lng] as LatLngExpression}
                icon={new L.Icon({
                  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                })}
              />
            )}
          </MapContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GPSInformation;
